import type { NotificationItem } from '@hemmafixa/shared';
import { useCallback } from 'react';
import { fill, useI18n } from '@/i18n/context';

export interface NotificationView {
  title: string;
  detail: string;
  /** Where clicking it goes */
  href: string;
}

/** The text and link for a notification, in the reader's language. Only type and data are stored. */
export function useDescribeNotification() {
  const { t, date } = useI18n();
  const n = t.notifications;
  return useCallback(
    (item: NotificationItem): NotificationView => {
      switch (item.type) {
        case 'new_job_match':
          return {
            title: n.newJobMatch,
            detail: `${item.data.title} · ${item.data.area}`,
            href: `/provider/jobs/${item.data.jobId}`,
          };
        case 'new_proposal':
          return {
            title: fill(n.newProposal, { provider: item.data.providerName }),
            detail: item.data.title,
            href: `/client/jobs/${item.data.jobId}`,
          };
        case 'proposal_chosen':
          return {
            title: fill(n.proposalChosen, { client: item.data.clientName }),
            detail: item.data.title,
            href: `/provider/jobs/${item.data.jobId}`,
          };
        case 'job_closed':
          return {
            title: item.data.reason === 'other_chosen' ? n.jobClosedOther : n.jobClosedCancelled,
            detail: item.data.title,
            href: `/provider/jobs/${item.data.jobId}`,
          };
        case 'trial_ending':
          return {
            title: fill(n.trialEnding, { date: date(item.data.endsAt) }),
            detail: n.trialEndingText,
            href: '/provider/billing',
          };
        case 'payment_failed':
        case 'grace_ending':
          return {
            title: n.paymentFailed,
            detail: fill(n.paymentFailedText, { date: date(item.data.graceEndsAt) }),
            href: '/provider/billing',
          };
        case 'access_paused':
          return { title: n.accessPaused, detail: n.accessPausedText, href: '/provider/billing' };
        case 'moderation_warning':
          return {
            title: n.moderationWarning,
            detail: n.moderationWarningText,
            href: '/client/notifications',
          };
        case 'new_message':
          return {
            title: fill(n.newMessage, { sender: item.data.senderName }),
            detail: item.data.title,
            href: `/${item.data.side}/messages?c=${item.data.conversationId}`,
          };
        case 'proposal_accepted':
          return {
            title: fill(n.proposalAccepted, { client: item.data.clientName }),
            detail: item.data.title,
            href: `/provider/jobs/${item.data.jobId}`,
          };
        case 'job_expiring':
          return { title: n.jobExpiring, detail: item.data.title, href: `/client/jobs/${item.data.jobId}` };
        case 'job_no_proposals':
          return {
            title: n.jobNoProposals,
            detail: item.data.title,
            href: `/client/jobs/${item.data.jobId}`,
          };
      }
    },
    [n, date],
  );
}
